import { useState, useEffect } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, FileDown, Award, Search, Printer } from "lucide-react";
import OpdPreview from "@/components/OpdPreview";

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30 focus:border-[#1E5AA8]";

export default function PrescriptionPad() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState("certificate");

  const [cert, setCert] = useState({ template: "fitness", patientName: "", age: "", days: "", from: "", to: "", diagnosis: "", extra: "" });

  const [rxSearch, setRxSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/meta").then((r) => setTemplates(r.data.certificate_templates)).catch(() => {}); }, []);

  const tpl = templates.find((t) => t.id === cert.template);
  const buildBody = () => {
    const name = cert.patientName || "____________";
    const age = cert.age || "__";
    switch (cert.template) {
      case "fitness":
        return `This is to certify that ${name} (Age ${age}) has been examined by me and is found to be medically FIT to resume normal duties/activities${cert.diagnosis ? ` following ${cert.diagnosis}` : ""}. ${cert.extra || ""}`;
      case "leave":
        return `This is to certify that ${name} (Age ${age}) is advised medical leave/rest for ${cert.days || "__"} day(s)${cert.from ? ` from ${cert.from}` : ""}${cert.to ? ` to ${cert.to}` : ""} on account of ${cert.diagnosis || "illness"}. ${cert.extra || ""}`;
      case "sickness":
        return `This is to certify that ${name} (Age ${age}) is suffering from ${cert.diagnosis || "illness"} and was under my treatment/observation. Rest and treatment advised for ${cert.days || "__"} day(s). ${cert.extra || ""}`;
      case "referral":
        return `Dear Colleague,\n\nKindly evaluate and manage ${name} (Age ${age})${cert.diagnosis ? `, provisionally diagnosed with ${cert.diagnosis}` : ""}. ${cert.extra || "Relevant clinical details are available on request."}\n\nThank you.`;
      case "travel":
        return `This is to certify that ${name} (Age ${age}) is medically fit to travel${cert.diagnosis ? ` despite ${cert.diagnosis}` : ""}. No contraindication to travel is noted at present. ${cert.extra || ""}`;
      default: return "";
    }
  };

  const exportCert = async (format) => {
    if (!cert.patientName) { toast.error("Enter patient name"); return; }
    await downloadFile("/certificates/export", "post", { title: tpl?.title || "Medical Certificate", body: buildBody(), format }, `certificate.${format}`);
    toast.success(`Certificate exported (${format.toUpperCase()})`);
  };

  const searchPatients = async (q) => {
    setRxSearch(q);
    if (q.length < 2) { setPatients([]); return; }
    const r = await api.get("/patients", { params: { q } });
    setPatients(r.data);
  };
  const loadPatient = async (pid) => {
    const r = await api.get(`/patients/${pid}`);
    const rx = r.data.prescriptions?.[0];
    if (!rx) { toast.error("No saved prescription for this patient"); return; }
    setSelected({ rx, patient: r.data.patient });
  };

  return (
    <div className="space-y-5" data-testid="prescriptions-tab">
      <div className="flex gap-2">
        <TabPill active={mode === "certificate"} onClick={() => setMode("certificate")} icon={Award} label="Medical Certificates" testId="pad-mode-certificate" />
        <TabPill active={mode === "opd"} onClick={() => setMode("opd")} icon={FileText} label="Reprint OPD Case Paper" testId="pad-mode-opd" />
      </div>

      {mode === "certificate" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Generate Certificate</h3>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Certificate Type</label>
            <select data-testid="select-cert-type" value={cert.template} onChange={(e) => setCert({ ...cert, template: e.target.value })} className={inp}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="col-span-2"><label className="text-xs text-slate-500">Patient Name</label><input data-testid="input-cert-name" value={cert.patientName} onChange={(e) => setCert({ ...cert, patientName: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-slate-500">Age</label><input value={cert.age} onChange={(e) => setCert({ ...cert, age: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-slate-500">No. of Days</label><input value={cert.days} onChange={(e) => setCert({ ...cert, days: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-slate-500">From</label><input type="date" value={cert.from} onChange={(e) => setCert({ ...cert, from: e.target.value })} className={inp} /></div>
              <div><label className="text-xs text-slate-500">To</label><input type="date" value={cert.to} onChange={(e) => setCert({ ...cert, to: e.target.value })} className={inp} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-500">Diagnosis / Reason</label><input value={cert.diagnosis} onChange={(e) => setCert({ ...cert, diagnosis: e.target.value })} className={inp} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-500">Additional Remarks</label><textarea value={cert.extra} onChange={(e) => setCert({ ...cert, extra: e.target.value })} rows={2} className={inp} /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="btn-cert-pdf" onClick={() => exportCert("pdf")} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73]"><FileDown className="w-4 h-4" /> Download PDF</button>
              <button data-testid="btn-cert-word" onClick={() => exportCert("docx")} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"><FileText className="w-4 h-4" /> Word</button>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-2xl bg-white border border-slate-200 shadow-md p-8">
            <div className="border-b-2 border-[#F59E0B] pb-3 flex justify-between items-start">
              <div><h2 className="font-display text-xl font-bold text-[#1E5AA8]">{user?.clinic_name || "MDvia Care"}</h2><p className="text-xs text-slate-500">{user?.clinic_address || ""}</p></div>
              <div className="text-right text-xs text-slate-500"><p className="font-semibold text-[#123C73]">{user?.full_name || user?.name}</p><p>{user?.qualifications}</p><p>Reg: {user?.registration_number}</p></div>
            </div>
            <h3 className="text-center font-display font-bold text-lg text-[#123C73] mt-6 uppercase">{tpl?.title}</h3>
            <p className="text-xs text-slate-400 mt-3">Date: {new Date().toLocaleDateString()}</p>
            <p className="text-sm text-slate-700 leading-7 mt-3 whitespace-pre-wrap">{buildBody()}</p>
            <div className="mt-12 text-right text-xs text-slate-500">
              <div className="flex items-end justify-end gap-2">
                {user?.stamp_image && <img src={user.stamp_image} alt="stamp" className="h-12 object-contain" />}
                {user?.signature_image && <img src={user.signature_image} alt="sign" className="h-10 object-contain" />}
              </div>
              <div className="inline-block border-t border-slate-400 pt-1 w-40 mt-1"><p className="font-semibold">{user?.full_name || user?.name}</p><p>Signature & Stamp</p></div>
            </div>
          </div>
        </div>
      )}

      {mode === "opd" && (
        <div className="space-y-4">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 no-print">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Find patient to reprint their latest OPD case paper</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="input-opd-search" value={rxSearch} onChange={(e) => searchPatients(e.target.value)} placeholder="Search by ID, name, phone…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {patients.map((p) => (
                <button key={p.patient_id} onClick={() => loadPatient(p.patient_id)} className="text-left p-3 rounded-xl border border-slate-100 hover:bg-[#EBF3FB]">
                  <div className="flex justify-between"><span className="font-medium text-slate-800">{p.name}</span><span className="text-xs font-mono-id text-[#1E5AA8]">{p.patient_id}</span></div>
                </button>
              ))}
            </div>
          </div>
          {selected && (
            <>
              <div className="no-print flex gap-2 justify-end">
                <button onClick={() => downloadFile(`/prescriptions/${selected.rx.id}/export?format=pdf`, "post", {}, "opd.pdf")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:bg-slate-50"><FileDown className="w-4 h-4" /> PDF</button>
                <button onClick={() => downloadFile(`/prescriptions/${selected.rx.id}/export?format=docx`, "post", {}, "opd.docx")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:bg-slate-50"><FileText className="w-4 h-4" /> Word</button>
                <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E5AA8] text-white text-sm font-semibold hover:bg-[#123C73]"><Printer className="w-4 h-4" /> Print</button>
              </div>
              <OpdPreview rx={selected.rx} patient={selected.patient} doctor={user} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TabPill({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#1E5AA8] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
